import { useState, useEffect } from 'react';
import {
  Table,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  Center,
  TextInput,
  createStyles,
  Modal,
  rem,
  Button,
  Box,
} from '@mantine/core';
import { IconSelector, IconChevronDown, IconChevronUp, IconSearch, IconTrash, IconEditCircle, IconUserCog, IconScanEye, IconScan, IconChecklist } from '@tabler/icons-react';
import usePagination from '../hooks/usePagination';
import Paginator from './Paginator';
import { useDisclosure } from '@mantine/hooks';
import ContainerSubmissionEditForm from './forms/ContainerSubmissionEditForm';
import { useNavigate } from 'react-router-dom';
import { path } from '../utils/CommonUtils';
import moment from 'moment/moment';
import ReviewStatusIndicator from './ReviewStatusIndicator';
import ReviewSubmissionForm from './forms/ReviewSubmissionForm';

const useStyles = createStyles((theme) => ({
  tableContainer: {
    width: '100%',
    position: 'relative',
    overflow: 'hidden'
  },
  th: {
    padding: 0
  },
  control: {
    width: '100%',
    padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-md)',
    '&hover': {
      backgroundColor: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6));'
    }
  },
  icon: {
    width: rem(21),
    height: rem(21),
    borderRadius: rem(21)
  },
}));

const tableKeys = [
  'user.companyName',
  'user.companyRole',
  'productName',
  'beverageType',
  'size',
  'materialType',
  'upc',
  'color',
  'dateSubmitted',
  'dateModified',
  'reviewStatusName',
];

function Th({ children, reversed, sorted, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <th className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group justify="space-between" noWrap>
          <Text fw={500} fz="xs" miw={150}>
            {children}
          </Text>
          <Center className={classes.icon}>
            <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </th>
  );
}

function filterData(data, search) {
  const query = search.toString().toLowerCase().trim();
  return data.filter((item) =>
    tableKeys.some((key) =>
      (path(item, key) || "").toString().toLowerCase().includes(query))
  );
}

function sortData(data, payload) {
  const { sortBy } = payload;

  if (!sortBy) {
    return filterData(data, payload.search);
  }

  return filterData(
    [...data].sort((a, b) => {
      if (payload.reversed) {
        return path(b, sortBy).toString().localeCompare(path(a, sortBy));
      }

      return path(a, sortBy).toString().localeCompare(path(b, sortBy));
    }),
    payload.search
  );
}

export const RecentRegistrationsTable = ({ tableData, maxRows = 10 }) => {
  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(tableData);
  const [selectedRow, setSelectedRow] = useState(null);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [activePage, pageCount, currentItems, onPageClick, setPageData] = usePagination(tableData, maxRows);
  const [editRowModalOpened, { open: openEditRowModal, close: closeEditRowModal }] = useDisclosure(false);
  const navigate = useNavigate();
  const { classes } = useStyles();

  useEffect(() => {
    setPageData(sortedData);
  }, [sortedData]);

  useEffect(() => {
    setSortedData(tableData);
  }, [tableData])

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(tableData, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event) => {
    const { value } = event.currentTarget;
    setSearch(value.toString());
    setSortedData(sortData(tableData, { sortBy, reversed: reverseSortDirection, search: value.toString() }));
  };

  const handleClickEdit = (id) => {
    setSelectedRow(currentItems.find(x => x.id == id));
    openEditRowModal();
  }

  const handleEditRow = (record) => {
    const idx = sortedData.findIndex(x => x.id == record.id);
    const registrationsFiltered = sortedData.filter(x => x.id != record.id);

    const newSortedData = [
      ...registrationsFiltered.slice(0, idx),
      record,
      ...registrationsFiltered.slice(idx)
    ];

    setSortedData(newSortedData)

    closeEditRowModal();
  }

  const rows = (currentItems || []).map((row, idx) => (
    <tr key={idx}>
      <td>{row.upc}</td>
      <td>{row.productName}</td>
      <td>{row.materialType}</td>
      <td>{row.size}</td>
      <td>{row.beverageType}</td>
      <td>{row.color}</td>
      <td>{row.user.companyName}</td>
      <td>{row.user.companyRole}</td>
      <td>{moment(row.dateSubmitted).format('YYYY-MM-DD')}</td>
      {/*<td>{moment(row.dateModified).format('YYYY-MM-DD')}</td>*/}
      <td>
        <ReviewStatusIndicator
          reviewNotes={row.reviewNotes}
          reviewStatusDate={row.reviewStatusDate}
          reviewStatusName={row.reviewStatusName}
          reviewStatusId={row.reviewStatusId}
        />
      </td>
      <td>
        <Group grow spacing='xs'>
          <Button compact color='dark' onClick={() => navigate(`/admin/users/${row.user.id}`)}><IconUserCog size={20} /></Button>
          <Button compact color='dark' onClick={() => handleClickEdit(row.id)}><IconChecklist size={20} /></Button>
        </Group>
      </td>
    </tr>
  ));

  return (
    <Box className={classes.tableContainer}>
      <Modal overlayProps={{ opacity: 0.3 }} size='lg' opened={editRowModalOpened} onClose={closeEditRowModal} title="Review Submission" centered>
        <Center>
          <ReviewSubmissionForm
            onSubmit={(record) => handleEditRow(record)}
            submissionRecord={selectedRow}
            />
        </Center>
      </Modal>
      <TextInput
        placeholder="Search by any field"
        mb="md"
        icon={<IconSearch style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
        value={search}
        onChange={handleSearchChange}
      />
      <ScrollArea w={'100%'} offsetScrollbars type='always' py='md' scrollbarSize={30}>
        <Table verticalSpacing="xs" fontSize='xs' striped highlightOnHover withColumnBorders withBorder miw={1200}>
          <thead>
            <tr>
              <Th
                sorted={sortBy === 'upc'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('upc')}
              >
                UPC
              </Th>  
              <Th
                sorted={sortBy === 'productName'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('productName')}
              >
                Product Name
              </Th>
              <Th
                sorted={sortBy === 'materialType'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('materialType')}
              >
                Material
              </Th>
              <Th
                sorted={sortBy === 'size'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('size')}
              >
                Size (oz)
              </Th>
              <Th
                sorted={sortBy === 'beverageType'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('beverageType')}
              >
                Beverage Type
              </Th>
              <Th
                sorted={sortBy === 'color'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('color')}
              >
                Color
              </Th>
              <Th
                sorted={sortBy === 'user.companyName'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('user.companyName')}
              >
                Company
              </Th>
              <Th
                sorted={sortBy === 'user.companyRole'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('user.companyRole')}
              >
                Role
              </Th>
              <Th
                sorted={sortBy === 'dateSubmitted'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('dateSubmitted')}
              >
                Date Submitted
              </Th>  
              {/*<Th*/}
              {/*  sorted={sortBy === 'dateModified'}*/}
              {/*  reversed={reverseSortDirection}*/}
              {/*  onSort={() => setSorting('dateModified')}*/}
              {/*>*/}
              {/*  Date Modified*/}
              {/*</Th>*/}
              <Th
                sorted={sortBy === 'reviewStatusName'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('reviewStatusName')}
              >
                Review Status
              </Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan={tableKeys.length}>
                  <Text fw={500} fz={20} ta="center" p={25}>
                    ...Nothing Found...
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </ScrollArea>
      <Paginator
        onPageClick={onPageClick}
        pageCount={pageCount}
        pageRangeDisplayed={5}
        marginPagesDisplayed={3}
        activePage={activePage}
      />
    </Box>
  );
}